import React, { useState, createContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { node, object } from 'prop-types';
import apps from '../appsConfig';
import fetchStoreConfig from '../utils/fetchStoreConfig';

import { getAuthSource, getGroups } from '../modules/login';

import { OKTA, RETAIL, LOGIN } from '../constants/common';
import {
  DISABLE_ATHLETE_MANAGEMENT,
  DISABLE_CASH_MANAGEMENT,
  DISABLE_EJ,
  DISABLE_NIKE_CASH,
  DISABLE_EMPLOYEE_MAINTENANCE,
  DISABLE_SIM,
  DISABLE_SOR,
  DISABLE_SELF_CHECKOUT,
  DISABLE_STORE_HOURS
} from '../appsConfig';


export const StoreConfigsContext = createContext({});

export const getInitialStoreConfig = () => ({
  disableAthleteManagement: {key: DISABLE_ATHLETE_MANAGEMENT, value: true},
  disableCashManagement: {key: DISABLE_CASH_MANAGEMENT, value: true},
  disableEJ: {key: DISABLE_EJ, value: true},
  disableNikeCash: {key: DISABLE_NIKE_CASH, value: true},
  disableSIM: {key: DISABLE_SIM, value: true},
  disableSOR: {key: DISABLE_SOR, value: true},
  disableSelfCheckout: {key: DISABLE_SELF_CHECKOUT, value: true},
  disableStoreHours: {key: DISABLE_STORE_HOURS, value: true},
  disableEmployeeMaintenance: {key: DISABLE_EMPLOYEE_MAINTENANCE, value: true}
});

export const getOktaStoreConfig = ({ groups, initialStoreConfig = getInitialStoreConfig() } = {}) => {
  if (!Array.isArray(groups)) return groups;

  const appAdGroups = {
    disableEJ: apps.electronicJournal.adGroups,
    disableSIM: apps.retailWebApp.adGroups,
    disableStoreHours: apps.storeHours.adGroups,
  };

  return Object.entries(appAdGroups).reduce((config, [key, adGroups]) => {
    const hasGroup = groups.some(group => adGroups.includes(group));
    return {
      ...config,
      [key]: { ...config[key], value: !hasGroup ? true : false }
    };
  }, initialStoreConfig);
};

export const getStoreConfig = async ({
  authSource,
  storeNumber,
  token,
  groups,
  getRetailStoreConfiguration = fetchStoreConfig,
  getOktaStoreConfiguration = getOktaStoreConfig,
} = {}) => {
  switch (authSource) {
    case RETAIL:
      return await getRetailStoreConfiguration({ storeNumber, token });
    case OKTA:
      return getOktaStoreConfiguration({ groups });
    default:
      return null; 
  };
};

const StoreConfigsProvider = ({ children, mockedValue }) => {
  const authSource = useSelector(getAuthSource);
  const groups = useSelector(getGroups);
  const { Provider } = StoreConfigsContext;
  
  const country = localStorage.getItem('trueStoreId.country');
  const store = localStorage.getItem('trueStoreId.storeNumber');
  const storeNumber = `${country}-${store}`;
  
  const [storeConfigs, setStoreConfigs] = useState(null);
  const [storeConfigsLoading, setStoreConfigsLoading] = useState(false);

  useEffect(() => {
    // Skip the real fetch when a mocked context value is provided (test-only escape hatch).
    if (mockedValue) return;

    const getStoreConfiguration = async () => {
      const token = JSON.parse(sessionStorage.getItem(LOGIN));

      // Don't call the retail API without a valid access token — the initial
      // Redux authSource defaults to RETAIL before the user has actually logged
      // in, which would fire an unauthenticated request and get a 401.
      if (authSource === RETAIL && !token?.value?.access_token) {
        return;
      }

      setStoreConfigsLoading(true);
      try {
        const storeConfiguration = await getStoreConfig({ authSource, storeNumber, token, groups });
        setStoreConfigs(storeConfiguration);
      } catch (err) {
        console.error('[StoreConfigsProvider] Failed to fetch store configuration:', err);
      } finally {
        setStoreConfigsLoading(false);
      }
    };

    getStoreConfiguration();
  }, [storeNumber, authSource, groups, mockedValue]);

  return (
    <Provider
      value={mockedValue ?? {
        storeConfigs,
        storeConfigsLoading
      }}
    >
      {children}
    </Provider>
  );
};

StoreConfigsProvider.defaultProps = {
  children: {},
  mockedValue: null,
};

StoreConfigsProvider.propTypes = {
  children: node,
  mockedValue: object
};

export default StoreConfigsProvider;